/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/**
 * Shared helpers for converting inventory / available-quantity values between
 * a product's base unit of measure (the UOM inventory is actually maintained in)
 * and any other unit of measure the product exposes (e.g. Vial -> Box).
 *
 * Used by:
 *  - buybox.view.tsx (PDP - PVO items with multiple UOMs)
 *  - <add the other module/page here once wired up>
 *
 * IMPORTANT - conversion direction:
 * Each non-base UOM entry in `productUnitsOfMeasure.result` carries ExtensionProperties:
 *   FROMUOMSYMBOL  - the base UOM inventory is stored in (e.g. "ea")
 *   TOUOMSYMBOL    - this UOM itself (e.g. "ml", "dz", "pr")
 *   NUMERATOR / DENOMINATOR - standard/interim ratio (e.g. 1/12 for dozen)
 *   FACTOR         - product-specific override ratio (e.g. 500 for "1 ea = 500 ml")
 *
 * Formula used to go FROM base qty TO the target UOM qty:
 *
 *     targetQty = baseQty * (NUMERATOR / DENOMINATOR) * FACTOR
 *
 * Verified against REAL production data (base UOM "ea", 179 available):
 *   dz (dozen): N=1 D=12 F=1   -> 179 * 1/12 * 1   = 14    ea/12 per dozen
 *   pr (pair):  N=1 D=2  F=1   -> 179 * 1/2  * 1   = 89    ea/2  per pair
 *   ml:         N=1 D=1  F=500 -> 179 * 1    * 500 = 89500 ea*500 per ml
 *   pcs/OB:     N=1 D=1  F=1   -> 179 * 1    * 1   = 179   identity
 *
 * CORRECTION: an earlier version of this file divided by FACTOR instead of
 * multiplying, based on an unverified guess about a Vial/Box case-pack scenario.
 * That guess was never actually confirmed against real in-stock/out-of-stock
 * behavior and turned out to be backwards - it caused in-stock "ea" items to
 * incorrectly show as out of stock once converted to "ml". The formula above
 * (multiply by FACTOR) is now confirmed against real production data across
 * four different UOMs. If you still use a Vial/Box-style product with a large
 * FACTOR, re-verify its available-quantity display now that the formula changed.
 */

/** Minimal shape needed from a ProductUnitOfMeasure ExtensionProperties entry. */
interface IExtensionPropertyValue {
    StringValue?: string;
    IntegerValue?: number;
    DecimalValue?: number;
    FloatValue?: number;
    LongValue?: number;
    BooleanValue?: boolean;
}

interface IExtensionProperty {
    Key?: string;
    Value?: IExtensionPropertyValue;
}

/** Minimal shape needed from a single entry in productUnitsOfMeasure.result. */
export interface IProductUnitOfMeasureLike {
    Symbol?: string;
    Description?: string;
    DecimalPrecision?: number;
    ExtensionProperties?: IExtensionProperty[];
}

export interface IUomConversion {
    symbol: string;
    fromUom: string;
    numerator: number;
    denominator: number;
    factor: number;
    decimalPrecision: number;
}

const getPrimitiveTextValue = (value: unknown): string => {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return value.toString();
    }
    return '';
};

const getExtensionPropertyNumericValue = (extensionProperties: IExtensionProperty[] | undefined, key: string): number | undefined => {
    const property = extensionProperties?.find(prop => prop?.Key === key);
    const value = property?.Value;
    if (!value) {
        return undefined;
    }
    if (value.IntegerValue !== undefined && value.IntegerValue !== null) return value.IntegerValue;
    if (value.DecimalValue !== undefined && value.DecimalValue !== null) return value.DecimalValue;
    if (value.FloatValue !== undefined && value.FloatValue !== null) return value.FloatValue;
    if (value.LongValue !== undefined && value.LongValue !== null) return value.LongValue;
    return undefined;
};

const getExtensionPropertyStringValue = (extensionProperties: IExtensionProperty[] | undefined, key: string): string =>
    getPrimitiveTextValue(extensionProperties?.find(prop => prop?.Key === key)?.Value?.StringValue);

/**
 * Builds a Symbol -> IUomConversion lookup from a productUnitsOfMeasure.result array.
 * Entries without an explicit FACTOR/NUMERATOR/DENOMINATOR (i.e. the base UOM itself,
 * which has no FROMUOMSYMBOL/TOUOMSYMBOL/FACTOR set) default to an identity conversion.
 *
 * @param uomList - productUnitsOfMeasure.result array from the product data source.
 * @returns Map keyed by UOM Symbol (e.g. "Box", "VL").
 */
export const getUomConversionMap = (uomList: IProductUnitOfMeasureLike[] | undefined): Map<string, IUomConversion> => {
    const conversions = new Map<string, IUomConversion>();
    (uomList ?? []).forEach(uom => {
        const symbol = getPrimitiveTextValue(uom?.Symbol);
        if (!symbol) {
            return;
        }
        const extensionProperties = uom?.ExtensionProperties;
        const numerator = getExtensionPropertyNumericValue(extensionProperties, 'NUMERATOR') ?? 1;
        const denominator = getExtensionPropertyNumericValue(extensionProperties, 'DENOMINATOR') || 1;
        const factor = getExtensionPropertyNumericValue(extensionProperties, 'FACTOR') || 1;
        const fromUom = getExtensionPropertyStringValue(extensionProperties, 'FROMUOMSYMBOL') || symbol;

        conversions.set(symbol, {
            symbol,
            fromUom,
            numerator,
            denominator,
            factor,
            decimalPrecision: uom?.DecimalPrecision ?? 0
        });
    });
    return conversions;
};

/**
 * Converts a quantity expressed in the BASE unit of measure (the UOM inventory is
 * actually maintained/returned in) into the given target UOM.
 *
 * Rounds DOWN to the target UOM's decimal precision so the displayed / allowed
 * quantity never overstates what's truly available (e.g. 250 vials at 100/box
 * shows as 2 boxes available, not 2.5).
 *
 * @param baseQuantity - Available quantity as returned by inventory (base UOM).
 * @param targetUomSymbol - The UOM the user currently has selected (e.g. "Box").
 * @param conversions - Map produced by getUomConversionMap().
 * @returns Available quantity expressed in targetUomSymbol.
 */
export const convertAvailableQuantityToUOM = (
    baseQuantity: number,
    targetUomSymbol: string,
    conversions: Map<string, IUomConversion>
): number => {
    // Symbol casing isn't guaranteed to match between where targetUomSymbol comes from
    // (e.g. a dropdown value or DefaultUnitOfMeasure) and productUnitsOfMeasure's Symbol
    // field - do a case-insensitive lookup so a casing mismatch never silently falls
    // through to "no conversion found" and returns the wrong (unconverted) quantity.
    const normalizedTarget = targetUomSymbol?.trim().toLowerCase();
    const conversion =
        conversions.get(targetUomSymbol) ??
        Array.from(conversions.values()).find(c => c.symbol.trim().toLowerCase() === normalizedTarget);

    if (!conversion || !baseQuantity) {
        return baseQuantity;
    }

    // targetQty = baseQty * (NUMERATOR / DENOMINATOR) * FACTOR
    // Confirmed correct against real production data (base UOM "ea"):
    //   dz: N=1 D=12 F=1   -> 179 * 1/12 * 1   = 14    (179 each ~ 14 dozen)
    //   pr: N=1 D=2  F=1   -> 179 * 1/2  * 1   = 89    (179 each ~ 89 pairs)
    //   ml: N=1 D=1  F=500 -> 179 * 1    * 500 = 89500 (179 each * 500 mL/each)
    const rawConverted = (baseQuantity * conversion.numerator * conversion.factor) / conversion.denominator;

    const precisionMultiplier = Math.pow(10, conversion.decimalPrecision ?? 0);
    // Floor, not round - never allow displaying/ordering more than truly available.
    return Math.floor(rawConverted * precisionMultiplier) / precisionMultiplier;
};

/**
 * Convenience one-shot helper: builds the conversion map and converts in a single call.
 * Useful on pages that only need a single conversion and don't want to memoize the map themselves.
 *
 * @param baseQuantity - Available quantity as returned by inventory (base UOM).
 * @param targetUomSymbol - The UOM the user currently has selected.
 * @param uomList - productUnitsOfMeasure.result array from the product data source.
 * @returns Available quantity expressed in targetUomSymbol.
 */
export const convertAvailableQuantityToUOMFromList = (
    baseQuantity: number,
    targetUomSymbol: string,
    uomList: IProductUnitOfMeasureLike[] | undefined
): number => convertAvailableQuantityToUOM(baseQuantity, targetUomSymbol, getUomConversionMap(uomList));

/**
 * Finds the "base" unit of measure symbol from a productUnitsOfMeasure.result array -
 * i.e. the UOM that inventory is actually maintained/returned in.
 *
 * Identified as the entry whose FROMUOMSYMBOL is absent, or equals its own Symbol -
 * since every OTHER UOM's ExtensionProperties declare FROMUOMSYMBOL pointing to this
 * one (e.g. "ml", "dz", "pr" all declare FROMUOMSYMBOL="ea", but "ea" itself has no
 * FROMUOMSYMBOL property at all).
 *
 * Falls back to the first UOM in the list if no clear base entry is found (e.g. a
 * single-UOM product, or unusual data where every entry declares a FROMUOMSYMBOL).
 *
 * Useful for defaulting a UOM dropdown/selection to the unit inventory is natively
 * tracked in, so the initial page render never needs an async conversion to be
 * correct - avoids a race where productUnitsOfMeasure hasn't loaded yet but the
 * dropdown already defaulted to a non-base unit (e.g. "ml").
 */
export const getBaseUomSymbol = (uomList: IProductUnitOfMeasureLike[] | undefined): string | undefined => {
    const list = uomList ?? [];
    if (list.length === 0) {
        return undefined;
    }

    const baseEntry = list.find(uom => {
        const symbol = getPrimitiveTextValue(uom?.Symbol).trim().toLowerCase();
        const fromUom = getExtensionPropertyStringValue(uom?.ExtensionProperties, 'FROMUOMSYMBOL').trim().toLowerCase();
        return !fromUom || fromUom === symbol;
    });

    return getPrimitiveTextValue(baseEntry?.Symbol) || getPrimitiveTextValue(list[0]?.Symbol);
};